import React from 'react';
import { Link } from 'react-router-dom';
import { PageRenderer } from '../components/site/PageRenderer';
import { Seo } from '../components/site/Seo';
import { useSiteContent } from '../context/SiteContentContext';
import { useContentfulCollection } from '../hooks/useContentfulCollection';

function normaliseLocation(value) {
  if (!value) return '';
  // If it looks like coordinates (e.g. "53.3916, -1.4735"), replace with Sheffield
  if (/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(value.trim())) {
    return 'Sheffield, UK';
  }
  return value;
}

function getJobSlug(job) {
  return (job.slug || job.title || 'job')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function formatJobReleaseAge(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const dayInMs = 1000 * 60 * 60 * 24;
  const daysAgo = Math.max(0, Math.floor((Date.now() - parsedDate.getTime()) / dayInMs));

  return `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
}

function JobCardSkeleton() {
  return (
    <article className='job-card job-card-skeleton' aria-hidden='true'>
      <span className='skeleton-line skeleton-line-small' />
      <span className='skeleton-line skeleton-line-title' />
      <span className='skeleton-line skeleton-line-meta' />
      <span className='skeleton-line' />
      <span className='skeleton-line skeleton-line-short' />
    </article>
  );
}

export function CareersPage() {
  const { siteContent } = useSiteContent();
  const page = siteContent.pages.careers;
  const { items: jobs, isLoading: isLoadingJobs } = useContentfulCollection('jobs', { pageSize: 50 });

  return (
    <>
      <Seo page={page} />
      <PageRenderer page={page} />
      <section className='content-section open-roles-section' id='open-roles'>
        <div className='section-header centered'>
          <h2>Open roles.</h2>
          <p className='section-copy'>Review the role, then apply directly.</p>
        </div>
        <div className='job-grid' aria-busy={isLoadingJobs}>
          {isLoadingJobs
            ? Array.from({ length: 3 }, (_, index) => <JobCardSkeleton key={`job-skeleton-${index}`} />)
            : null}
          {!isLoadingJobs && jobs.length
            ? jobs.map((job) => {
            const releaseAge = formatJobReleaseAge(job.publishedAt || job.date);
            const metaItems = [
              job.location ? { label: 'Location', value: normaliseLocation(job.location) } : null,
              job.schedule || job.employmentType ? { label: 'Schedule', value: job.schedule || job.employmentType } : null,
            ].filter(Boolean);

            return (
              <Link
                key={getJobSlug(job)}
                to={`/jobs/${getJobSlug(job)}`}
                className='job-card-link'
                aria-label={job.title}
              >
                <article className='job-card'>
                  <div className='job-card-copy'>
                    {releaseAge ? <p className='card-eyebrow'>{releaseAge}</p> : null}
                    <h3>{job.title}</h3>
                    {metaItems.length ? (
                      <dl className='job-meta-list'>
                        {metaItems.map((item) => (
                          <div key={item.label} className='job-meta-item'>
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                  <div className='job-card-actions'>
                    <span className='secondary-cta'>View details</span>
                    <a
                      className='primary-cta'
                      href='#job-application'
                      data-cta-modal='jobApplication'
                      data-cta-role={job.title}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Apply
                    </a>
                  </div>
                </article>
              </Link>
            );
          })
            : null}
          {!isLoadingJobs && !jobs.length ? (
            <div className='job-empty-state'>
              <p className='card-eyebrow'>Open roles</p>
              <p>There are no jobs live right now.</p>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
